import { deriveBucketEnd, toFiniteNumber } from "~/shared/kpi"

import type { LtvMetricMode } from "../constants"

/**
 * Pure cohort-matrix math for the LTV Cohorts report. All date handling works
 * on `YYYY-MM-DD` ISO strings in UTC — deliberately NOT dayjs, whose locale is
 * configured with Sunday week starts while cube week buckets follow the
 * company's reporting week. Bucket starts always come from cube result rows,
 * so offsets are exact bucket arithmetic, never locale week math.
 */

export type CohortGranularity = "month" | "week"

/** One cube result row: a (cohort bucket, activity bucket) pair. */
export type CohortMatrixRow = {
  cohortStart: string
  activityStart: string
  netRevenue: number
  firstOrderRevenue: number
  newCustomers: number
  activeCustomers: number
  orders: number
}

export type CohortRow = {
  cohortStart: string
  /** Cohort size — the sum of anchor-row new_customers. */
  newCustomers: number
  /** The first orders' own net revenue (anchor rows). */
  firstOrderRevenue: number
  /**
   * Cumulative net revenue (INCLUDING the first order) by period offset,
   * gaps filled by carrying the previous value forward. Index = offset.
   */
  cumulativeNetRevenue: number[]
}

const DAY_MS = 86_400_000

/** `2026-07-01T00:00:00.000` (cube time key) -> `2026-07-01`. */
export function dateKeyToIso(value: unknown): string {
  return String(value ?? "").slice(0, 10)
}

function utcMs(iso: string): number {
  return Date.UTC(
    Number(iso.slice(0, 4)),
    Number(iso.slice(5, 7)) - 1,
    Number(iso.slice(8, 10)),
  )
}

function isoFromUtcMs(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10)
}

/**
 * Period offset between two bucket starts of the same granularity. Month
 * buckets are calendar months; week buckets are exact 7-day multiples apart
 * (both dates come from the same cube week granularity).
 */
export function bucketOffset(
  cohortStart: string,
  activityStart: string,
  granularity: CohortGranularity,
): number {
  if (granularity === "month") {
    return (
      (Number(activityStart.slice(0, 4)) - Number(cohortStart.slice(0, 4))) *
        12 +
      (Number(activityStart.slice(5, 7)) - Number(cohortStart.slice(5, 7)))
    )
  }
  return Math.round((utcMs(activityStart) - utcMs(cohortStart)) / (7 * DAY_MS))
}

/** Bucket start shifted forward by `offset` periods. */
export function addOffset(
  bucketStart: string,
  offset: number,
  granularity: CohortGranularity,
): string {
  if (granularity === "month") {
    const year = Number(bucketStart.slice(0, 4))
    const monthIndex = Number(bucketStart.slice(5, 7)) - 1 + offset
    return isoFromUtcMs(Date.UTC(year, monthIndex, 1))
  }
  return isoFromUtcMs(utcMs(bucketStart) + offset * 7 * DAY_MS)
}

/**
 * A cell is mature only when its ENTIRE activity bucket has elapsed —
 * strictly before today, so a bucket ending yesterday is mature and a bucket
 * ending today is not. Masking (not zero-filling) immature cells is what
 * keeps "$0 activity" distinguishable from "not yet measurable".
 */
export function isCellMature(
  cohortStart: string,
  offset: number,
  granularity: CohortGranularity,
  todayIso: string,
): boolean {
  return (
    deriveBucketEnd(addOffset(cohortStart, offset, granularity), granularity) <
    todayIso
  )
}

/** Largest mature offset for a cohort (-1 when even offset 0 is immature). */
export function maxMatureOffset(
  cohortStart: string,
  granularity: CohortGranularity,
  todayIso: string,
): number {
  let offset = -1
  // 400 periods ≈ 33 years monthly / 7.6 years weekly — loop guard only.
  while (
    offset < 400 &&
    isCellMature(cohortStart, offset + 1, granularity, todayIso)
  ) {
    offset += 1
  }
  return offset
}

/**
 * Groups raw (cohort bucket, activity bucket) rows into one row per cohort
 * with cumulative revenue by offset. Offsets beyond `maxOffset` are ignored;
 * negative offsets cannot occur (the dbt model guards activity_date >=
 * md_date) and are skipped defensively.
 */
export function buildCohortRows(
  rows: CohortMatrixRow[],
  granularity: CohortGranularity,
  maxOffset: number,
): CohortRow[] {
  const byCohort = new Map<
    string,
    { newCustomers: number; firstOrderRevenue: number; net: number[] }
  >()

  for (const row of rows) {
    let entry = byCohort.get(row.cohortStart)
    if (!entry) {
      entry = { newCustomers: 0, firstOrderRevenue: 0, net: [] }
      byCohort.set(row.cohortStart, entry)
    }
    entry.newCustomers += row.newCustomers
    entry.firstOrderRevenue += row.firstOrderRevenue

    const offset = bucketOffset(row.cohortStart, row.activityStart, granularity)
    if (offset < 0 || offset > maxOffset) continue
    entry.net[offset] = (entry.net[offset] ?? 0) + row.netRevenue
  }

  return Array.from(byCohort.entries())
    .map(([cohortStart, entry]) => {
      const cumulativeNetRevenue: number[] = []
      let running = 0
      for (let offset = 0; offset <= maxOffset; offset += 1) {
        running += entry.net[offset] ?? 0
        cumulativeNetRevenue.push(running)
      }
      return {
        cohortStart,
        newCustomers: entry.newCustomers,
        firstOrderRevenue: entry.firstOrderRevenue,
        cumulativeNetRevenue,
      }
    })
    .sort((a, b) => a.cohortStart.localeCompare(b.cohortStart))
}

/**
 * Accumulated per-customer value for one cell. Null when the cohort is empty
 * or the offset is out of range — maturity masking is the CALLER's concern
 * (via isCellMature) so exports and future forecast fills can reuse this.
 */
export function cohortCellValue(
  row: CohortRow,
  offset: number,
  mode: LtvMetricMode,
): number | null {
  if (row.newCustomers <= 0) return null
  const cumulative = row.cumulativeNetRevenue[offset]
  if (cumulative === undefined) return null
  const adjustment = mode === "incremental" ? row.firstOrderRevenue : 0
  return (cumulative - adjustment) / row.newCustomers
}

/** Average first-order value for the cohort (the "First order" column). */
export function firstOrderValue(row: CohortRow): number | null {
  if (row.newCustomers <= 0) return null
  return row.firstOrderRevenue / row.newCustomers
}

/**
 * Customer-weighted average across every cohort mature at `offset` — the
 * bottom "Average" row and the "Avg N month" summary cards. Weighting by
 * cohort size (not a mean of means) matches how a merged cohort would read.
 */
export function weightedAverageAtOffset(
  rows: CohortRow[],
  offset: number,
  mode: LtvMetricMode,
  granularity: CohortGranularity,
  todayIso: string,
): { value: number; cohortCount: number } | null {
  let revenue = 0
  let customers = 0
  let cohortCount = 0
  for (const row of rows) {
    if (row.newCustomers <= 0) continue
    if (!isCellMature(row.cohortStart, offset, granularity, todayIso)) continue
    const cumulative = row.cumulativeNetRevenue[offset]
    if (cumulative === undefined) continue
    const adjustment = mode === "incremental" ? row.firstOrderRevenue : 0
    revenue += cumulative - adjustment
    customers += row.newCustomers
    cohortCount += 1
  }
  if (customers <= 0) return null
  return { value: revenue / customers, cohortCount }
}

/**
 * Coerces one cube result row into a CohortMatrixRow. Cube returns measure
 * values as STRINGS on some transport paths — every value goes through
 * toFiniteNumber before any arithmetic.
 */
export function toMatrixRow(
  raw: Record<string, unknown>,
  keys: {
    cohortKey: string
    activityKey: string
    netRevenueKey: string
    firstOrderRevenueKey: string
    newCustomersKey: string
    activeCustomersKey: string
    ordersKey: string
  },
): CohortMatrixRow {
  return {
    cohortStart: dateKeyToIso(raw[keys.cohortKey]),
    activityStart: dateKeyToIso(raw[keys.activityKey]),
    netRevenue: toFiniteNumber(raw[keys.netRevenueKey]),
    firstOrderRevenue: toFiniteNumber(raw[keys.firstOrderRevenueKey]),
    newCustomers: toFiniteNumber(raw[keys.newCustomersKey]),
    activeCustomers: toFiniteNumber(raw[keys.activeCustomersKey]),
    orders: toFiniteNumber(raw[keys.ordersKey]),
  }
}
